import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, ScrollView } from 'react-native';

import CachedImage from '@components/CachedImage/CachedImage';
import { Box, Text } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { LocalFile, TeamRole } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useRefetchOnFocus } from '@hooks/useRefetchOnFocus';
import { useTeamDetailFromQuery } from '@hooks/useTeamDetailFromQuery';
import {
  getAvatarColor,
  getInitials,
} from '@screens/Projects/projectInvitationPreviewShared';
import theme from '@themes/theme';
import {
  APP_STORE_URL,
  detectAppInstallation,
  isMobileBrowser,
} from '@utils/mobileDeepLink';

type TeamWelcomeScreenProps = {
  route: {
    params: {
      teamId: string;
    };
  };
};

const ROLE_LABEL: Record<TeamRole, string> = {
  [TeamRole.Owner]: 'Owner',
  [TeamRole.Admin]: 'Admin',
  [TeamRole.Member]: 'Member',
};

export const TeamWelcomeScreen: React.FC<TeamWelcomeScreenProps> = ({
  route,
}) => {
  const { teamId } = route.params;
  const { me } = useMe();
  const { t } = useTranslation('models');

  useEffect(() => {
    if (isMobileBrowser()) {
      return detectAppInstallation('com.eloveit.tasktag:///', APP_STORE_URL);
    }
  }, []);

  // getPublicTeam, not getTeam (TA-10850): this screen is reached straight
  // after accepting an invite, when the membership may not be visible to the
  // reader yet — getTeam answers "The requested team could not be found" then.
  const {
    fullTeam: team,
    loading,
    error,
    refetch,
  } = useTeamDetailFromQuery({
    teamUUId: teamId,
    fetchPolicy: 'cache-first',
  });

  useRefetchOnFocus(refetch);
  const firstName = me?.firstName ?? me?.name?.split(' ')[0] ?? '';

  if (loading) {
    return (
      <Box flex={1} alignItems='center' justifyContent='center'>
        <ActivityIndicatorLoading />
      </Box>
    );
  }

  if (error || !team) {
    return (
      <Box flex={1} alignItems='center' justifyContent='center' padding='m'>
        <Text
          variant='webBodySecondary'
          color='textSecondary'
          textAlign='center'>
          {t('onBoarding.networkError')}
        </Text>
      </Box>
    );
  }

  return (
    <Box flex={1} backgroundColor='grey01' alignItems='center'>
      <Box flex={1} width='100%' style={{ maxWidth: 480 }}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{ paddingBottom: 200 }}>
          {/* Header — checkmark + welcome text */}
          <Box
            backgroundColor='white'
            alignItems='center'
            paddingTop='xl'
            paddingBottom='xl'
            style={{
              borderBottomLeftRadius: theme.borderRadii.l,
              borderBottomRightRadius: theme.borderRadii.l,
            }}>
            <Box
              width={72}
              height={72}
              backgroundColor='greenSecondaryMild'
              alignItems='center'
              justifyContent='center'
              mb='s'
              style={{ borderRadius: theme.spacing.xl }}>
              <Icon
                name='Check'
                width={40}
                height={40}
                color='greenSecondary'
              />
            </Box>
            <Text
              variant='labelLarge'
              color='textPrimary'
              textAlign='center'
              mb='xxs'>
              {firstName
                ? t('onBoarding.teamWelcome.welcomeToTeamWithName', {
                    firstName,
                  })
                : t('onBoarding.teamWelcome.welcomeToTeam')}
            </Text>
            <Text
              variant='webBodySecondary'
              color='textSecondary'
              textAlign='center'>
              {t('onBoarding.teamWelcome.youreNowPartOf', {
                teamName: team.name,
              })}
            </Text>
          </Box>

          {/* Team identity card + member list */}
          <Box
            backgroundColor='white'
            borderRadius='m'
            margin='m'
            style={{ overflow: 'hidden' }}>
            {/* Team identity row */}
            <Box
              flexDirection='row'
              alignItems='center'
              justifyContent='space-between'
              p='m'
              style={{ gap: theme.spacing.s }}>
              <Box>
                <Text variant='labelLarge' color='textPrimary'>
                  {team.name}
                </Text>
                <Text variant='metadata1' color='grey04'>
                  {`ID: ${team.id}`}
                </Text>
              </Box>
              {team.avatarFile ? (
                <CachedImage
                  avatarType='team'
                  avatarId={team.avatarFile.id}
                  image={team.avatarFile as unknown as LocalFile}
                  width={80}
                  height={44}
                  borderRadius={theme.borderRadii.xs}
                  resizeMode='contain'
                />
              ) : (
                <Box
                  width={40}
                  height={40}
                  backgroundColor='black'
                  borderRadius='xs'
                  alignItems='center'
                  justifyContent='center'>
                  <Icon name='TT40' width={40} height={40} />
                </Box>
              )}
            </Box>

            <Box height={1} backgroundColor='grey02' />

            {/* Members */}
            {team.users.map((member, index) => {
              const primaryRole = member.teamRoles?.[0];
              const roleLabel = primaryRole ? ROLE_LABEL[primaryRole] : null;
              const name = member.name ?? '';
              const initials = getInitials(name);
              const avatarColor = getAvatarColor(index);

              return (
                <Box key={member.id}>
                  <Box
                    flexDirection='row'
                    alignItems='center'
                    px='m'
                    py='s'
                    style={{ gap: theme.spacing.s }}>
                    <Box
                      width={36}
                      height={36}
                      alignItems='center'
                      justifyContent='center'
                      style={{
                        borderRadius: 18,
                        backgroundColor: avatarColor,
                        flexShrink: 0,
                      }}>
                      <Text variant='webSmall' color='white'>
                        {initials}
                      </Text>
                    </Box>
                    <Box flex={1}>
                      <Text variant='webBodySecondary' color='textPrimary'>
                        {name}
                      </Text>
                      {!!roleLabel && (
                        <Text variant='metadata' color='grey05'>
                          {roleLabel}
                        </Text>
                      )}
                    </Box>
                  </Box>
                  {index < team.users.length - 1 && (
                    <Box
                      height={1}
                      backgroundColor='grey01'
                      marginHorizontal='m'
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </ScrollView>

        {/* Download banner — fixed at bottom */}
        <Box
          position='absolute'
          style={{
            bottom: theme.spacing.m,
            left: theme.spacing.m,
            right: theme.spacing.m,
          }}
          backgroundColor='black'
          borderRadius='m'
          p='m'>
          <Text variant='labelEmphasized' color='white' mb='xxs'>
            {t('onBoarding.teamWelcome.youreAllSet')}
          </Text>
          <Text
            variant='webBodySecondary'
            color='white'
            mb='m'
            style={{ opacity: 0.8 }}>
            {t('onBoarding.teamWelcome.downloadAppBody')}
          </Text>
          <Button
            variant='greenSecondary'
            fullWidth
            onPress={() => Linking.openURL(APP_STORE_URL)}
            accessibilityLabel={t('onBoarding.teamWelcome.downloadTheApp')}>
            {t('onBoarding.teamWelcome.downloadTheApp')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
